import { useEffect, useRef, useCallback } from 'react'
import { useVoiceStore } from '../stores/voiceStore'
import { useAuthStore } from '../stores/authStore'
import { useLivekitStore } from '../stores/livekitStore'
import { getSocket } from '../services/socket'
import { recordTranscriptionStart, recordTranscriptionRestart, recordTranscriptionBackfill } from '../services/transcriptionDiag'
import { Track, type Room } from 'livekit-client'

const CHUNK_INTERVAL_MS = 250
/** OSPROD-267 chunk-flow watchdog: MediaRecorder can freeze with
 *  `state === 'recording'` and a live track while `ondataavailable`
 *  silently stops firing (observed after join/leave republish churn and
 *  background-tab throttling). The old health check trusted
 *  recorder.state, so this looked "healthy" forever. Chunks normally
 *  arrive every CHUNK_INTERVAL_MS — 8s of silence while unmuted is a
 *  stall, not a pause (opus encodes silence; chunks flow regardless of
 *  whether the user is speaking). */
const CHUNK_STALL_RESTART_MS = 8000
/** Backfill request schedule. The server scopes `voice:getTranscripts` by
 *  socket-room membership, so a request that beats our own `voice:join`
 *  gets an empty reply.
 *
 *  OSPROD-267 retest: this used to be ONE blind 1500ms timer. `joinChannel()`
 *  sets `currentChannelId` optimistically, so the timer starts before
 *  `livekitStore.connect()` resolves — and a cold connect (token POST + WS +
 *  mic publish + denoiser warmup) routinely overruns 1.5s. Lose that race and
 *  the panel stays empty for the ENTIRE session with no retry, while whoever
 *  joined earlier sees every line. That asymmetry is what got reported as
 *  "one participant sees the transcript, another doesn't" — no role or
 *  permission is involved. Retry on a backoff and stop at the first non-empty
 *  reply instead of betting the session on a single guess. */
const BACKFILL_RETRY_DELAYS_MS = [300, 1200, 2500, 5000, 9000]
/** Re-attempt startTranscription this many times when an early-return
 *  branch fires (track not yet live, recorder error, clone failed).
 *  Without this, a single race during the initial join silently kills
 *  transcription for the whole session — the user reported peers
 *  hearing them but their voice not appearing in the transcript until
 *  a tab refresh, which is exactly this bug. */
const START_RETRY_DELAYS_MS = [200, 400, 800, 1600, 3200, 5000]

/**
 * Server-side live transcription via Deepgram.
 *
 * Replaces the old Web Speech API path which (a) didn't work in Electron's
 * bundled Chromium because it lacks the privileged Google Speech endpoint,
 * (b) gave inconsistent results across browsers. The new flow:
 *
 *   1. Clone the user's published mic track from LiveKit (so we don't
 *      open a second `getUserMedia` — that fails on USB mics under
 *      Electron + Windows).
 *   2. Wrap the cloned track in a MediaRecorder with `audio/webm;codecs=opus`.
 *   3. Every CHUNK_INTERVAL_MS, ship the recorded chunk via socket.io to
 *      the server, which proxies it to Deepgram's live WebSocket.
 *   4. Final transcripts come back via the existing `transcript:new` socket
 *      event — same path as the old client-emitted ones, so the rest of
 *      the UI doesn't need to change.
 */
// OSPROD-91: `roomOverride` lets a DM call (os-messenger dmCallStore.room)
// drive transcription from its OWN LiveKit room. Group voice channels call
// this with no override and fall back to livekitStore.room as before.
export function useTranscription(channelId: string | undefined, roomOverride?: Room | null) {
  const currentUser = useAuthStore((s) => s.user)
  const addTranscript = useVoiceStore((s) => s.addTranscript)
  const setLivePartial = useVoiceStore((s) => s.setLivePartial)
  const clearLivePartial = useVoiceStore((s) => s.clearLivePartial)
  const setTranscriptCleanedText = useVoiceStore((s) => s.setTranscriptCleanedText)
  const mergeTranscripts = useVoiceStore((s) => s.mergeTranscripts)
  const prependTranscripts = useVoiceStore((s) => s.prependTranscripts)
  const setTranscriptPageState = useVoiceStore((s) => s.setTranscriptPageState)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const clonedTrackRef = useRef<MediaStreamTrack | null>(null)
  const startedChannelRef = useRef<string | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const retryAttemptRef = useRef<number>(0)
  // OSPROD-267: wall-clock of the last ondataavailable event — the
  // chunk-flow watchdog's ground truth (recorder.state lies, see
  // CHUNK_STALL_RESTART_MS).
  const lastChunkAtRef = useRef<number>(0)
  // Latest preconditions snapshot — set by the caller, read by the
  // retry timer so it bails out if the user has muted / left while the
  // retry was queued.
  const preconditionsRef = useRef<{ channelId: string | undefined; shouldBeOn: boolean }>({ channelId: undefined, shouldBeOn: false })

  // Keep listening for transcripts (server emits these — could be from us
  // or from any other speaker). The server emits two events per burst:
  // `transcript:partial` while the speaker is still talking (live UI
  // update, no DB write yet) and `transcript:new` once they pause and
  // the buffer is committed.
  useEffect(() => {
    if (!channelId) return
    const socket = getSocket()
    const onNew = (entry: { id: string; speakerId?: string; speakerName: string; text: string; cleanedText?: string | null; timestamp: number }) => {
      void currentUser
      addTranscript(entry)
      // addTranscript already drops the partial for entry.speakerId, so
      // the live "in-progress" line collapses into the committed entry.
    }
    const onPartial = (entry: { id: string; speakerId: string; speakerName: string; text: string; timestamp: number }) => {
      setLivePartial(entry)
    }
    // The `transcript-cleanup` worker emits this once gpt-4o-mini has
    // returned a cleaned-up version of an utterance. Patch the bubble
    // in place; the panel reads `cleanedText ?? text`.
    const onCleaned = (payload: { id: string; cleanedText: string }) => {
      setTranscriptCleanedText(payload.id, payload.cleanedText)
    }
    // L7: Deepgram interim_results — same shape as partial; rendered
    // identically (greyed/italic in TranscriptionPanel). Gated server-
    // side by env TRANSCRIPT_INTERIM=1; off by default.
    socket.on('transcript:new', onNew)
    socket.on('transcript:partial', onPartial)
    socket.on('transcript:interim', onPartial)
    socket.on('transcript:cleaned', onCleaned)
    return () => {
      socket.off('transcript:new', onNew)
      socket.off('transcript:partial', onPartial)
      socket.off('transcript:interim', onPartial)
      socket.off('transcript:cleaned', onCleaned)
    }
  }, [channelId, addTranscript, setLivePartial, clearLivePartial, setTranscriptCleanedText, currentUser])

  // OSPROD-267: history backfill. Live `transcript:new` events only cover
  // lines spoken while THIS socket was in the `voice:<id>` room — a late
  // joiner, a refreshed tab, or a client whose socket blipped mid-call
  // permanently missed everything before that ("the same speaker's lines
  // appear for one participant but not another"). On entering the room
  // (and again on every socket reconnect, to converge lines missed while
  // offline) fetch the current occupancy-session's lines and MERGE them
  // into the store (id-deduped — live entries win over backfilled copies).
  const currentVoiceChannelId = useVoiceStore((s) => s.currentChannelId)
  useEffect(() => {
    if (!channelId) return
    // Channel calls: only when the user is actually in this voice channel
    // (the view also renders for browsing users, who aren't in the socket
    // room and would just get an empty response). DM calls (roomOverride)
    // are joined to the socket room by the server at dm:call:accept.
    if (!roomOverride && currentVoiceChannelId !== channelId) return
    const socket = getSocket()
    let timers: number[] = []
    // An empty reply is ambiguous — it means EITHER "nothing said yet" OR
    // "your socket isn't in voice:<id> yet". Only a non-empty reply proves we
    // are in the room, so that's what stops the schedule.
    let gotLines = false
    const onBackfill = (entries: Array<{ id: string; speakerId?: string; speakerName: string; text: string; cleanedText?: string | null; timestamp: number }>) => {
      if (!Array.isArray(entries) || entries.length === 0) return
      gotLines = true
      timers.forEach((t) => window.clearTimeout(t))
      timers = []
      recordTranscriptionBackfill(entries.length)
      console.info(`[Transcription] backfill merged ${entries.length} line(s) (channel=${channelId})`)
      mergeTranscripts(entries)
    }
    const request = () => {
      try { socket.emit('voice:getTranscripts', { channelId }) } catch { /* socket not ready */ }
    }
    const schedule = () => {
      timers.forEach((t) => window.clearTimeout(t))
      timers = BACKFILL_RETRY_DELAYS_MS.map((delay) =>
        window.setTimeout(() => { if (!gotLines) request() }, delay),
      )
    }
    // OSPROD-295: the real entry count for this occupancy, and whether older
    // pages exist. Separate event so the initial-load payload stays a bare
    // array for clients that predate pagination.
    const onMeta = (payload: { channelId?: string; total?: number; hasMore?: boolean }) => {
      if (payload?.channelId && payload.channelId !== channelId) return
      setTranscriptPageState({
        transcriptsTotal: typeof payload?.total === 'number' ? payload.total : null,
        transcriptsHasMore: !!payload?.hasMore,
      })
    }
    // A page of OLDER lines, requested by scrolling up. Prepend rather than
    // merge so the live-append path is untouched.
    const onPage = (payload: { entries?: Array<{ id: string; speakerId?: string; speakerName: string; text: string; cleanedText?: string | null; timestamp: number }>; hasMore?: boolean }) => {
      const entries = Array.isArray(payload?.entries) ? payload.entries : []
      if (entries.length) prependTranscripts(entries)
      setTranscriptPageState({ transcriptsHasMore: !!payload?.hasMore, transcriptsLoadingOlder: false })
    }
    socket.on('voice:transcripts', onBackfill)
    socket.on('voice:transcripts:meta', onMeta)
    socket.on('voice:transcripts:page', onPage)
    schedule()
    // Reconnect = we may have missed broadcasts while the socket was down;
    // useSocket re-emits voice:join on 'connect', so run the whole schedule
    // again (the re-join races us exactly like the initial one does).
    const onReconnect = () => {
      gotLines = false
      schedule()
    }
    socket.on('connect', onReconnect)
    // OSPROD-295: the panel asks for an older page by bumping a counter in the
    // store; the emit lives here, next to the rest of the transcript socket
    // wiring, so the component never imports the socket.
    let lastSeq = useVoiceStore.getState().transcriptsLoadOlderSeq
    // `subscribe` is absent on the shallow store mocks some tests use — same
    // defensive shape as VoiceManager's reconciler.
    const unsubLoadOlder = typeof useVoiceStore.subscribe === 'function'
      ? useVoiceStore.subscribe((st) => {
          if (st.transcriptsLoadOlderSeq === lastSeq) return
          lastSeq = st.transcriptsLoadOlderSeq
          const oldest = st.transcripts[0]
          if (!oldest) { setTranscriptPageState({ transcriptsLoadingOlder: false }); return }
          try {
            socket.emit('voice:getTranscripts', {
              channelId,
              before: { createdAt: oldest.timestamp, id: oldest.id },
            })
          } catch {
            setTranscriptPageState({ transcriptsLoadingOlder: false })
          }
        })
      : null
    return () => {
      timers.forEach((t) => window.clearTimeout(t))
      if (unsubLoadOlder) unsubLoadOlder()
      socket.off('voice:transcripts', onBackfill)
      socket.off('voice:transcripts:meta', onMeta)
      socket.off('voice:transcripts:page', onPage)
      socket.off('connect', onReconnect)
    }
  }, [channelId, roomOverride, currentVoiceChannelId, mergeTranscripts, prependTranscripts, setTranscriptPageState])

  /** Schedules another tryStart() unless we've used up all retry slots
   *  or the caller has cancelled (via stopTranscription). */
  const scheduleRetry = useCallback(() => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
    const attempt = retryAttemptRef.current
    if (attempt >= START_RETRY_DELAYS_MS.length) {
      console.warn('[Transcription] giving up after', attempt, 'retries')
      return
    }
    const delay = START_RETRY_DELAYS_MS[attempt]
    retryAttemptRef.current = attempt + 1
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null
      // Re-check preconditions before retrying — user may have muted or
      // left while we were waiting.
      const { channelId: cid, shouldBeOn } = preconditionsRef.current
      if (!cid || !shouldBeOn) return
      // Re-enter the same start path. tryStart() will re-schedule if
      // it bails again.
      tryStartRef.current?.()
    }, delay)
  }, [])

  const tryStartRef = useRef<((explicitTrack?: MediaStreamTrack) => void) | null>(null)

  const startTranscription = useCallback(() => {
    // Reset retry counter — this is the first attempt of a fresh
    // start cycle (caller decided preconditions are now met).
    retryAttemptRef.current = 0
    preconditionsRef.current = { channelId, shouldBeOn: true }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }

    const tryStart = (explicitTrack?: MediaStreamTrack) => {
      if (!channelId || !currentUser) return
      if (startedChannelRef.current === channelId) return

      const room = roomOverride ?? useLivekitStore.getState().room
      if (!room) {
        scheduleRetry()
        return
      }

      // OSPROD-137: a denoiser AGC-stopgap track-swap (mediaContext
      // `_applyTrackSwap`) hands us the FRESH track via the
      // `voice:micTrackSwapped` event. Prefer it — after a `replaceTrack`
      // the publication getter `pub.track.mediaStreamTrack` can stay
      // pointing at the now-`ended` old track (VOICE_CRITICAL_INVARIANTS
      // #1/#2), so re-reading it here would bail forever and the live
      // transcript would never recover from the swap.
      const micPub = room.localParticipant.getTrackPublication(Track.Source.Microphone)
      const sourceTrack = explicitTrack ?? micPub?.track?.mediaStreamTrack
      if (!sourceTrack) {
        // Mic isn't published yet — schedule a retry; LiveKit's
        // LocalTrackPublished may also wake us up via the useEffect
        // depending on `localAudioTrack`.
        scheduleRetry()
        return
      }
      // setMicrophoneEnabled is async — when the user undeafens, the
      // useEffect that calls us can re-run with isMuted=false before
      // LiveKit has finished republishing. The track we'd otherwise
      // grab is the OLD one that's already in 'ended' state. Cloning
      // it and handing the clone to MediaRecorder throws
      // "Failed to execute 'start' on 'MediaRecorder'". Bail out and
      // retry — by next attempt the new live track is published.
      if (sourceTrack.readyState !== 'live') {
        scheduleRetry()
        return
      }

      // Clone the LiveKit-published mic track so MediaRecorder gets
      // its own independent track copy. Without cloning, MediaRecorder
      // would mute the upstream when stopped. Clones share the
      // underlying source — Chromium / Electron both allow this.
      let cloned: MediaStreamTrack
      try {
        cloned = sourceTrack.clone()
      } catch (err) {
        console.warn('[Transcription] track.clone failed:', err)
        scheduleRetry()
        return
      }
      if (cloned.readyState !== 'live') {
        try { cloned.stop() } catch { /* noop */ }
        scheduleRetry()
        return
      }
      clonedTrackRef.current = cloned

      const stream = new MediaStream([cloned])
      let recorder: MediaRecorder
      try {
        recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' })
      } catch (err) {
        console.warn('[Transcription] MediaRecorder unsupported:', err)
        try { cloned.stop() } catch { /* noop */ }
        clonedTrackRef.current = null
        // MediaRecorder unsupported is a permanent failure — no point
        // retrying; the codec just isn't there.
        return
      }
      recorderRef.current = recorder

      const socket = getSocket()
      socket.emit('transcript:start', { channelId })

      recorder.ondataavailable = async (event) => {
        // Any event proves the encoder loop is alive — count it for the
        // chunk-flow watchdog even when the payload is empty.
        lastChunkAtRef.current = Date.now()
        if (!event.data || event.data.size === 0) return
        try {
          const buf = await event.data.arrayBuffer()
          // volatile: a chunk emitted while the socket is down must be DROPPED,
          // not queued — socket.io's sendBuffer is unbounded, and a half-dead
          // client (media alive, socket flapping for hours) accumulated
          // gigabytes of audio chunks in memory (2026-08-06, 50GB client).
          // A dropped chunk is a hole in the transcript; the Deepgram session
          // is dead across a disconnect anyway.
          socket.volatile.emit('transcript:audio-chunk', { channelId, chunk: buf })
        } catch (err) {
          console.warn('[Transcription] chunk forward failed:', err)
        }
      }

      recorder.onerror = (event) => {
        console.warn('[Transcription] recorder error:', event)
      }

      // recorder.start() can still throw even after we've vetted the
      // track — race conditions in Chromium's media pipeline mean
      // readyState='live' isn't a hard guarantee. Catch, unwind, and
      // schedule a retry so a transient race doesn't disable
      // transcription for the whole session.
      try {
        recorder.start(CHUNK_INTERVAL_MS)
      } catch (err) {
        console.warn('[Transcription] recorder.start failed:', err)
        try { recorder.stop() } catch { /* noop */ }
        try { cloned.stop() } catch { /* noop */ }
        recorderRef.current = null
        clonedTrackRef.current = null
        try { socket.emit('transcript:stop', { channelId }) } catch { /* noop */ }
        scheduleRetry()
        return
      }
      // Success — clear retry state.
      startedChannelRef.current = channelId
      retryAttemptRef.current = 0
      // Seed the watchdog clock so a just-started recorder isn't
      // instantly judged stalled before its first chunk lands.
      lastChunkAtRef.current = Date.now()
      recordTranscriptionStart()
      // OSPROD-137: positive telemetry. Until now the hook only logged
      // FAILURES, so "empty [Transcription] logs" was ambiguous between
      // "never started" and "started fine" — which cost real triage time.
      // Log the successful start + which track it bound to.
      console.info(`[Transcription] recorder started (channel=${channelId}, src=${explicitTrack ? 'track-swap' : 'publication'})`)
    }
    tryStartRef.current = tryStart
    tryStart()
  }, [channelId, currentUser, scheduleRetry, roomOverride])

  const stopTranscription = useCallback(() => {
    // Cancel any pending retry — caller is asking us to stop.
    preconditionsRef.current = { channelId: undefined, shouldBeOn: false }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
    retryAttemptRef.current = 0
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      try { recorder.stop() } catch { /* noop */ }
    }
    recorderRef.current = null
    if (clonedTrackRef.current) {
      try { clonedTrackRef.current.stop() } catch { /* noop */ }
      clonedTrackRef.current = null
    }
    if (startedChannelRef.current) {
      try { getSocket().emit('transcript:stop', { channelId: startedChannelRef.current }) } catch { /* noop */ }
    }
    startedChannelRef.current = null
  }, [])

  // Wake retry up when LiveKit re-publishes the local audio track. The
  // useLivekitStore.localAudioTrack reference flips to a new track
  // instance on republish — covers (a) user re-enabling mic AFTER all
  // backoff slots fired (the original case), AND (b) OSPROD-137: every
  // republish during an active session (denoiser swap, AGC stopgap,
  // device change, connection-quality recovery) leaves our cloned
  // track bound to the now-defunct source — recorder.state may still
  // say 'recording' but no chunks reach Deepgram because the upstream
  // is dead. Repro Sasha reported: транскрипт работает, потом кто-то
  // join/leave → republish ticks → транскрипт молчит до рефреша.
  //
  // Fix: when the live track reference changes WHILE we're already
  // started, tear down the stale recorder and start fresh against the
  // new track. Don't bail on `startedChannelRef === cid` like the
  // original code did — that was the silent-loss path.
  const localAudioTrack = useLivekitStore((s) => s.localAudioTrack)
  useEffect(() => {
    if (!localAudioTrack) return
    const { channelId: cid, shouldBeOn } = preconditionsRef.current
    if (!cid || !shouldBeOn) return
    if (startedChannelRef.current === cid) {
      // Already started for this channel — but the track just changed
      // under us. Stop the stale recorder + clone, then re-arm with the
      // new track. startTranscription is the right entry because it
      // resets retry counters AND preconditions (idempotent for the
      // current channel).
      const recorder = recorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.stop() } catch { /* noop */ }
      }
      recorderRef.current = null
      if (clonedTrackRef.current) {
        try { clonedTrackRef.current.stop() } catch { /* noop */ }
        clonedTrackRef.current = null
      }
      startedChannelRef.current = null
      // Don't emit transcript:stop on the socket — the server-side
      // Deepgram session is still valid for this channel and we want
      // to keep reusing it across the republish, so the next audio
      // chunk just slots in. (transcript:stop would tear the session
      // down server-side.)
      recordTranscriptionRestart('track-republish')
      console.info(`[Transcription] local audio track republished — re-arming recorder (channel=${cid})`)
    }
    retryAttemptRef.current = 0
    tryStartRef.current?.()
  }, [localAudioTrack])

  // OSPROD-137: re-arm on a denoiser AGC-stopgap track-swap. mediaContext
  // `_applyTrackSwap` replaces the published mic track via `replaceTrack`,
  // which fires NO `LocalTrackPublished` (the `localAudioTrack` effect above
  // never re-fires) and leaves `pub.track.mediaStreamTrack` stale
  // (VOICE_CRITICAL_INVARIANTS #1/#2). The active recorder's clone dies and
  // can't re-acquire via the getter → the live transcript silently stops
  // until a full republish (mic toggle / reload). mediaContext hands us the
  // FRESH track via the `voice:micTrackSwapped` window event; tear down the
  // stale recorder and re-arm the MediaRecorder directly on that track.
  // Confirmed root cause + verified live on prod 2026-05-29. Dispatch side
  // is kill-switched (`voiceTranscriptSwapResync`), so this listener is a
  // no-op when the swap stops emitting.
  useEffect(() => {
    const onSwap = (e: Event) => {
      const detail = (e as CustomEvent).detail as { room?: Room; track?: MediaStreamTrack } | undefined
      const swapped = detail?.track
      if (!swapped || swapped.readyState !== 'live') return
      // Only re-arm for OUR room (channel = livekitStore.room; a DM call
      // passes its own room via roomOverride). Compare by reference — both
      // sides share the same LiveKit Room instance.
      const myRoom = roomOverride ?? useLivekitStore.getState().room
      if (!myRoom || detail?.room !== myRoom) return
      const { channelId: cid, shouldBeOn } = preconditionsRef.current
      if (!cid || !shouldBeOn) return
      // Tear down the stale recorder + clone bound to the pre-swap track.
      const recorder = recorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.stop() } catch { /* noop */ }
      }
      recorderRef.current = null
      if (clonedTrackRef.current) {
        try { clonedTrackRef.current.stop() } catch { /* noop */ }
        clonedTrackRef.current = null
      }
      startedChannelRef.current = null
      retryAttemptRef.current = 0
      recordTranscriptionRestart('track-swap')
      // Re-arm directly on the swapped-in track — do NOT trust the
      // publication getter (stale post-`replaceTrack`). Keep the server-side
      // Deepgram session alive (no `transcript:stop`) so chunks just continue.
      tryStartRef.current?.(swapped)
    }
    window.addEventListener('voice:micTrackSwapped', onSwap)
    return () => window.removeEventListener('voice:micTrackSwapped', onSwap)
  }, [roomOverride])

  // OSPROD-267: server-initiated re-arm. When the server's Deepgram WS
  // dies mid-session (network blip, Deepgram-side close), the server
  // drops the dead session and emits `transcript:session-dead` to the
  // feeding socket. Our recorder looks perfectly healthy in that state —
  // chunks keep flowing into the void — so no client-side watchdog can
  // catch it. Tear down and re-arm: tryStart re-emits transcript:start,
  // which opens a fresh Deepgram connection whose WebM header lines up
  // with the fresh MediaRecorder stream.
  useEffect(() => {
    const socket = getSocket()
    const onSessionDead = (payload: { channelId?: string }) => {
      const { channelId: cid, shouldBeOn } = preconditionsRef.current
      if (!cid || !shouldBeOn) return
      if (payload?.channelId && payload.channelId !== cid) return
      console.warn(`[Transcription] server reported Deepgram session dead — re-arming recorder (channel=${cid})`)
      recordTranscriptionRestart('session-dead')
      const recorder = recorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.stop() } catch { /* noop */ }
      }
      recorderRef.current = null
      if (clonedTrackRef.current) {
        try { clonedTrackRef.current.stop() } catch { /* noop */ }
        clonedTrackRef.current = null
      }
      startedChannelRef.current = null
      retryAttemptRef.current = 0
      tryStartRef.current?.()
    }
    socket.on('transcript:session-dead', onSessionDead)
    return () => { socket.off('transcript:session-dead', onSessionDead) }
  }, [])

  // OSPROD-137: visibility / focus recovery. Chrome may throttle or
  // suspend MediaRecorder timers when the tab is hidden, and even after
  // visible there are reports of recorder.state == 'recording' but
  // ondataavailable never firing again — the only way to recover is a
  // fresh recorder. Window-minimize → return is one of Sasha's
  // reproducer paths in the ticket, exactly this case. We restart
  // unconditionally on visible/focus when transcription should be on;
  // the underlying socket transcript:start is idempotent on the
  // server (it closes the existing Deepgram session before opening a
  // new one — see socket.ts:transcript:start handler).
  useEffect(() => {
    const restartIfNeeded = () => {
      const { channelId: cid, shouldBeOn } = preconditionsRef.current
      if (!cid || !shouldBeOn) return
      // Don't fight the existing retry cycle — scheduleRetry already
      // owns the timer, walking through START_RETRY_DELAYS_MS. Bumping
      // a fresh tryStart in parallel would emit transcript:start twice
      // server-side and reset the backoff counter to zero on every
      // tick, defeating the backoff.
      if (retryTimerRef.current) return
      const recorder = recorderRef.current
      const cloned = clonedTrackRef.current
      // Only restart if the recorder is in a suspicious state — already-
      // healthy recorders shouldn't get torn down on every focus event
      // (avoids re-publishing a transcript:start storm during active
      // typing alt-tab cycles). "Suspicious" = recorder gone, recorder
      // not recording, OR cloned track ended.
      const healthy = !!recorder
        && recorder.state === 'recording'
        && !!cloned
        && cloned.readyState === 'live'
      if (healthy) {
        // OSPROD-267 chunk-flow watchdog: "recording" + live track is NOT
        // proof of life — MediaRecorder can freeze with ondataavailable
        // silently gone (join/leave republish churn, background-tab timer
        // throttling). Chunks flow every CHUNK_INTERVAL_MS regardless of
        // speech (opus encodes silence), so a long gap = a real stall.
        const sinceChunk = Date.now() - lastChunkAtRef.current
        if (lastChunkAtRef.current === 0 || sinceChunk < CHUNK_STALL_RESTART_MS) return
        console.warn(`[Transcription] chunk flow stalled for ${Math.round(sinceChunk / 1000)}s while recording — restarting recorder (channel=${cid})`)
        recordTranscriptionRestart('chunk-stall')
      } else {
        recordTranscriptionRestart('unhealthy-recorder')
      }
      const recorderToStop = recorderRef.current
      if (recorderToStop && recorderToStop.state !== 'inactive') {
        try { recorderToStop.stop() } catch { /* noop */ }
      }
      recorderRef.current = null
      if (clonedTrackRef.current) {
        try { clonedTrackRef.current.stop() } catch { /* noop */ }
        clonedTrackRef.current = null
      }
      startedChannelRef.current = null
      retryAttemptRef.current = 0
      tryStartRef.current?.()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') restartIfNeeded()
    }
    const onFocus = () => restartIfNeeded()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)
    // Periodic health check — covers cases neither visibility nor
    // republish caught (e.g. silent MediaRecorder freeze in long
    // sessions). 5s cadence: short enough that an unlucky drop is
    // un-noticed for at most one short utterance, long enough that
    // healthy sessions never feel the work.
    const interval = window.setInterval(restartIfNeeded, 5000)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onFocus)
      window.clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    return () => {
      stopTranscription()
    }
  }, [stopTranscription])

  return { startTranscription, stopTranscription }
}
